import React, { useState } from "react";
import { Link } from "react-router-dom";
import { parent_routes } from "../../../router/all_routes";
import "bootstrap/dist/css/bootstrap.min.css";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { modifyParentPassword } from "../../../../api/parents";
import Swal from "sweetalert2";
import Avvvatars from "avvvatars-react";

// pour restreaindre l'accès à ce composant
import requireAuthentification from "../../../../core/common/autorisations/requireAuthentification";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { useChildrenDetails } from "../../../../core/common/context/ChildrenDetailsContext";
import { getStudentArchivesById } from "../../../../api/archives";

type PasswordField =
  | "oldPassword"
  | "newPassword"
  | "confirmPassword"
  | "currentPassword";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
    newPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
    confirmPassword: z.string().min(8, {
      message: "Le mot de passe doit comporter au moins 8 caractères",
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: " Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "Le nouveau mot de passe doit être différent de l'ancien",
    path: ["newPassword"],
  });

type changePasswordFormData = z.infer<typeof changePasswordSchema>;

const ParentProfile = () => {
  const routes = parent_routes;
  const { childrenDetails } = useChildrenDetails();
  const userData = useSelector((state: any) => state.auth.user);
  const [childrenArchives, setChildrenArchives] = useState<
    Record<string, any[]>
  >({});
  const [expandedChildren, setExpandedChildren] = useState<
    Record<string, boolean>
  >({});
  const [isArchivesLoading, setIsArchivesLoading] = useState(false);

  const [passwordVisibility, setPasswordVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
    currentPassword: false,
  });

  // vérifier si un objet est vide
  const isEmpty = (obj: any) => Object.keys(obj).length === 0;

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  // 3. Formulaire changement de mot de passe
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: errorsPassword },
    reset: resetPassword,
    clearErrors: clearErrorsPassword,
  } = useForm<changePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  // Fonctions de soumissions des formulaires d'édition

  const onSubmitPassword = async (data: changePasswordFormData) => {
    try {
      const { currentPassword, newPassword } = data;
      const response = await modifyParentPassword({
        oldPassword: currentPassword,
        newPassword,
      });
      if (!response) {
        Swal.fire({
          title: "Erreur",
          text: "La modification du mot de passe a échoué.",
          icon: "error",
        });
        throw new AxiosError("La modification du mot de passe a échoué.");
      }
      Swal.fire({
        title: "Succès",
        text: "Votre mot de passe a été changé avec succès.",
        icon: "success",
      });
      document.getElementById("closeBtn-passwordModal")?.click();
      resetPassword();
    } catch (error: any) {
      // console.log(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  React.useEffect(() => {
    const fetchChildrenArchives = async () => {
      if (!childrenDetails || childrenDetails.length === 0) {
        setChildrenArchives({});
        return;
      }

      setIsArchivesLoading(true);
      try {
        const archivesByChild = await Promise.all(
          childrenDetails.map(async (child: any) => {
            try {
              const response = await getStudentArchivesById(child.id);
              return {
                id: child.id,
                archives: response?.data ?? [],
              };
            } catch (error) {
              // console.error(
              //   `Erreur récupération archives enfant ${child.id}:`,
              //   error,
              // );
              return { id: child.id, archives: [] };
            }
          }),
        );

        setChildrenArchives(
          archivesByChild.reduce(
            (acc, entry) => ({ ...acc, [entry.id]: entry.archives }),
            {},
          ),
        );
      } catch (error: any) {
        // console.error("Erreur récupération des archives des enfants:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Une erreur s'est produite lors du chargement des archives des enfants",
        );
      } finally {
        setIsArchivesLoading(false);
      }
    };

    fetchChildrenArchives();
  }, [childrenDetails]);

  return (
    <div>
      <>
        {/* Page Wrapper */}
        <div className="page-wrapper">
          <div className="content">
            <div className="d-md-flex d-block align-items-center justify-content-between border-bottom pb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Profile</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.parentDashboard}>Accueil</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Mon Profil
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            {errorsPassword.root && (
              <div className="alert alert-danger">
                {errorsPassword.root.message}
              </div>
            )}
            <div className=" d-block mt-3">
              <div className="settings-right-sidebar me-md-3 border-0">
                <div className="card">
                  <div className="card-header">
                    <h5>Information Personnel</h5>
                  </div>
                  <div className="card-body ">
                    <div className="settings-profile-upload">
                      <span className="profile-pic">
                        <Avvvatars
                          value={`${userData?.firstname?.charAt(0) || ""}${
                            userData?.lastname?.charAt(0) || ""
                          }`}
                          style="character"
                          size={60}
                        />
                      </span>
                      <div className="title-upload">
                        <h5>
                          {userData?.firstname} {userData?.lastname}
                        </h5>
                        <p className="text-primary me-2">
                          {userData?.email || userData?.number || "Parent"}
                        </p>
                      </div>
                    </div>

                    <dl className="row mb-0">
                      <dt className="col-4 fw-medium text-dark mb-3">Rôle</dt>
                      <dd className="col-8 mb-3">Parent</dd>
                      <dt className="col-4 fw-medium text-dark mb-3">Sexe</dt>
                      <dd className="col-8 mb-3">
                        {userData?.sexe || "Non renseigné"}
                      </dd>
                      <dt className="col-4 fw-medium text-dark mb-3">
                        Nombre d'enfants
                      </dt>
                      <dd className="col-8 mb-3">
                        {childrenDetails?.length || 0}
                      </dd>
                      {userData?.birthday && (
                        <>
                          <dt className="col-4 fw-medium text-dark mb-3">
                            Naissance
                          </dt>
                          <dd className="col-8 mb-3">
                            {new Date(userData?.birthday).toLocaleDateString(
                              "fr-FR",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              },
                            )}
                          </dd>
                        </>
                      )}
                      <dt className="col-4 fw-medium text-dark mb-3">Email</dt>
                      <dd className="col-8 mb-3">
                        {userData?.email || "Aucun"}
                      </dd>
                      <dt className="col-4 fw-medium text-dark mb-3">
                        Téléphone
                      </dt>
                      <dd className="col-8 mb-3">
                        {userData?.number || "Aucun"}
                      </dd>
                      <dt className="col-4 fw-medium text-dark mb-3">
                        Adresse
                      </dt>
                      <dd className="col-8 mb-3">
                        {userData?.adresse || "Aucune"}
                      </dd>
                    </dl>
                  </div>
                </div>
                <div className="card mt-3">
                  <div className="card-header">
                    <h5>Enfants</h5>
                  </div>
                  <div className="card-body">
                    {childrenDetails && childrenDetails.length > 0 ? (
                      childrenDetails.map((child: any) => {
                        const archives = childrenArchives[child.id] ?? [];
                        const expanded = expandedChildren[child.id] ?? false;
                        const sortedArchives = [...archives].sort(
                          (a: any, b: any) => {
                            if (
                              a.AcademicYear?.label &&
                              b.AcademicYear?.label
                            ) {
                              return a.AcademicYear.label.localeCompare(
                                b.AcademicYear.label,
                              );
                            }
                            return (a.id_academicYear || "").localeCompare(
                              b.id_academicYear || "",
                            );
                          },
                        );
                        return (
                          <div
                            key={child.id}
                            className="mb-4 pb-2 border-bottom"
                          >
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 ">
                              <div
                                className=" d-flex align-items-center justify-content-between mb-2 w-100"
                                onClick={() =>
                                  setExpandedChildren((prev) => ({
                                    ...prev,
                                    [child.id]: !prev[child.id],
                                  }))
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span className="profile-pic">
                                    <Avvvatars
                                      value={`${child.firstname?.charAt(0) || ""}${
                                        child.lastname?.charAt(0) || ""
                                      }`}
                                      style="character"
                                      size={40}
                                    />
                                  </span>
                                  <h6 className="mb-1">
                                    {child.firstname} {child.lastname}
                                  </h6>
                                </div>
                                <i
                                  className={`fas ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`}
                                ></i>
                              </div>
                            </div>

                            {expanded && (
                              <div className="mt-3">
                                <h6
                                  className=" d-flex align-items-center justify-content-between mb-2"
                                  onClick={() =>
                                    setExpandedChildren((prev) => ({
                                      ...prev,
                                      [child.id]: !prev[child.id],
                                    }))
                                  }
                                >
                                  <span className="me-2">
                                    Parcours Historique
                                  </span>
                                </h6>

                                {isArchivesLoading && archives.length === 0 ? (
                                  <p>Chargement des archives...</p>
                                ) : sortedArchives.length > 0 ? (
                                  sortedArchives.map(
                                    (archive: any, idx: number) => (
                                      <div
                                        key={
                                          archive.id ||
                                          archive.id_academicYear ||
                                          idx
                                        }
                                        className="border rounded p-3 mb-2 bg-light"
                                      >
                                        <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                                          <span className="badge bg-secondary">
                                            {archive.AcademicYear?.label ||
                                              archive.id_academicYear ||
                                              "Année inconnue"}
                                          </span>
                                        </div>
                                        <div>
                                          <strong>Classe</strong>
                                          <div>
                                            {archive?.Class?.Level
                                              ?.abbreviation || ""}{" "}
                                            {archive?.Class?.label ||
                                              "Non renseignée"}
                                          </div>
                                        </div>
                                      </div>
                                    ),
                                  )
                                ) : (
                                  <p>
                                    Aucun parcours historique pour cet enfant.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p>Aucun enfant associé pour le moment.</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-fill ps-0 border-0">
                <form>
                  <div className="d-md-flex">
                    <div className="flex-fill">
                      <div className="card">
                        <div className="card-body d-flex justify-content-between align-items-center mb-4">
                          <h5>Mot de passe</h5>
                          <Link
                            to="#"
                            className="btn btn-primary btn-sm"
                            data-bs-toggle="modal"
                            data-bs-target="#change_password"
                            onClick={() => {
                              if (!isEmpty(errorsPassword))
                                clearErrorsPassword();
                            }}
                          >
                            Changer
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Page Wrapper */}

        {/* Change Password */}
        <div className="modal fade" id="change_password">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Changer le mot de passe</h4>
                <button
                  id="closeBtn-passwordModal"
                  type="button"
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <form
                className="needs-validation"
                onSubmit={handleSubmitPassword(onSubmitPassword)}
              >
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Mot de passe actuel
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_currentPassword_password"
                            {...registerPassword("currentPassword")}
                            type={
                              passwordVisibility.oldPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.oldPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("oldPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.currentPassword && (
                        <div className="text-danger">
                          {errorsPassword.currentPassword.message}
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="form-label">
                          Nouveau mot de passe
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_newPassword_password"
                            {...registerPassword("newPassword")}
                            type={
                              passwordVisibility.newPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.newPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("newPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.newPassword && (
                        <div className="text-danger">
                          {errorsPassword.newPassword.message}
                        </div>
                      )}
                      <div className="mb-0">
                        <label className="form-label">
                          Confirmer le mot de passe
                        </label>
                        <div className="pass-group d-flex">
                          <input
                            id="edit_confirmPassword_password"
                            {...registerPassword("confirmPassword")}
                            type={
                              passwordVisibility.confirmPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                          />
                          <span
                            className={`ti toggle-passwords ${
                              passwordVisibility.confirmPassword
                                ? "ti-eye"
                                : "ti-eye-off"
                            }`}
                            onClick={() =>
                              togglePasswordVisibility("confirmPassword")
                            }
                          ></span>
                        </div>
                      </div>
                      {errorsPassword.confirmPassword && (
                        <div className="text-danger">
                          {errorsPassword.confirmPassword.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Annuler
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    // data-bs-dismiss="modal"
                  >
                    Enregistrer les modifications
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Change Password */}
      </>
    </div>
  );
};

export default requireAuthentification(ParentProfile);
